import classNames from 'classnames';
import { debounce } from 'debounce';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ShiftScrollTip from '../../assets/shift-scroll-tip.svg';
import { isPhoneOrTablet } from '../../helpers/analytics.helper';
import generalHelper from '../../helpers/general.helper';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import useResize from '../../hooks/useResize';
import { useUser } from '../../hooks/users';
import LoadingImage from '../general/LoadingImage';
import ShortkeyListener from '../shortkey/ShortkeyListener';
import { TooltipCreator } from './generators/base';
import {
  useChartLoadingAnimation,
  useEChartInit,
  useItemWidth,
  useOnDemandLoading,
} from './hooks';
import IXICTradingView from './IXICTradingView';
import { LEFT_SIDE_OFFSET, RIGHT_SIDE_OFFSET } from './modules/constants';
import { makeChart } from './modules/general';
import { getMarkPointWidth, useMarkTooltip } from './modules/mark-point';
import { calcDataZoomStartPercentage } from './modules/zoomRange';
import { insertPreviousClose } from './oldStuffToBeRemoved/process';
import { PriceViewerChartProps, ZoomButtonAction } from './types';

const ReactJsonLazy = React.lazy(() => import('react-json-view'));
const Div = styled.div`
  .shift-to-zoom-tip {
    position: absolute;
    pointer-events: none;
    top: 0;
    left: 50%;
    opacity: 0;
    transform: translate(-50%, -100%);
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    padding: 0.2rem 0.5rem;
    font-size: 0.8rem;
    border-radius: 3px;
    transition: all 200ms;
    text-align: center;

    &.shift-scroll-tip-visible {
      top: 50px;
      transform: translate(-50%, 0);
      opacity: 1;
      transition-delay: 0;
    }

    img {
      height: 1.5rem;
      filter: invert(100%);
    }
  }
`;
const ChartContainer = styled.div`
  width: 100%;
`;

const adminShortKeys = [
  {
    section: 'admin',
    key: 'ctrl+d',
    description: 'View EChart options',
  },
];

const TIMES_SHOWN_SHIFT_SCROLL_TOP = 'TIMES_SHOWN_SHIFT_SCROLL_TOP';
const getTimesShownShiftScrollTop = () => {
  try {
    const value = Number.parseInt(
      localStorage.getItem(TIMES_SHOWN_SHIFT_SCROLL_TOP)
    );
    if (Number.isNaN(value)) return 0;
    return value;
  } catch {
    return 0;
  }
};
const incrementTimesShownShiftScrollTop = () =>
  localStorage.setItem(
    TIMES_SHOWN_SHIFT_SCROLL_TOP,
    String(getTimesShownShiftScrollTop() + 1)
  );

const PriceViewerChart: React.FC<PriceViewerChartProps> = ({
  loading = false,
  onLoadMore = generalHelper.noop,
  priceData,
  chartGenerators,
  selectedPeriodPreset,
  bullishColor,
  bearishColor,
  useIXICTradingViewPrice,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [markTooltipCreaters, setMarkTooltipCreaters] = useState<
    TooltipCreator[]
  >([]);
  const [displayLegendData, setDisplayLegendData] = useState(true);

  const isMobile = isPhoneOrTablet();

  const { t } = useTranslation();

  const chart = useEChartInit(chartRef);
  const [shiftScrollTipVisible, showShiftScrollTip] = useState(false);

  // Pass the loading property to ECharts
  useChartLoadingAnimation(chart, loading);

  useEffect(() => {
    insertPreviousClose(priceData);
  }, [priceData]);

  const features = useFeatureFlags();

  const isAdmin = useUser()?.isAdmin;

  const itemWidth = useItemWidth(chart, priceData.length);
  const markPointWidth = getMarkPointWidth(itemWidth);
  const zoomLevel = itemWidth > 20 ? 'zoomed-in' : 'zoomed-out';
  // Generate the tooltip for mark points,
  // this is not a part of ECharts so we need to render them ourselves.
  const markTooltipElement = useMarkTooltip(chart, markTooltipCreaters);
  // Setup on loading mechanism
  const updateChart = useCallback(
    (range?: [number, number]) => {
      if (chart) {
        const length = priceData.length;
        const originalDataZoomStart = calcDataZoomStartPercentage(
          priceData,
          selectedPeriodPreset,
          RIGHT_SIDE_OFFSET,
          LEFT_SIDE_OFFSET
        );
        const originalDataZoomEnd =
          ((length + LEFT_SIDE_OFFSET) * 100) /
          (length + RIGHT_SIDE_OFFSET + LEFT_SIDE_OFFSET);
        const effectiveRange =
          length && range
            ? range
            : [originalDataZoomStart, originalDataZoomEnd];

        const zoomButtonHandler = (zoomAction: ZoomButtonAction) => {
          const dataZoom = chart.getOption().dataZoom;
          let { start, end } = dataZoom[0];
          switch (zoomAction) {
            case 'zoom-in': {
              start = start + 2;

              break;
            }
            case 'zoom-out': {
              start = start - 2;

              break;
            }
            case 'zoom-restore': {
              start = originalDataZoomStart;
              end = originalDataZoomEnd;

              break;
            }
            // No default
          }
          chart.dispatchAction({
            type: 'dataZoom',
            start,
            end,
          });
        };
        const { options, height, markTooltipCreaters } = makeChart(
          chartGenerators,
          priceData,
          bullishColor,
          bearishColor,
          zoomLevel,
          t,
          isMobile,
          markPointWidth,
          zoomButtonHandler,
          effectiveRange[0],
          effectiveRange[1],
          displayLegendData,
          !!markTooltipElement,
          useIXICTradingViewPrice,
          features.includes('SHIFT_SCROLL_TO_ZOOM')
        );
        chart.setOption(options, true);
        setHeight(height);
        setMarkTooltipCreaters(markTooltipCreaters);
      }
    },
    [
      chart,
      priceData,
      selectedPeriodPreset,
      chartGenerators,
      bullishColor,
      bearishColor,
      zoomLevel,
      t,
      isMobile,
      markPointWidth,
      displayLegendData,
      markTooltipElement,
      useIXICTradingViewPrice,
      features,
    ]
  );
  const { forgetUserZoomRange } = useOnDemandLoading(
    chart,
    onLoadMore,
    priceData.length,
    updateChart
  );

  // forget the user zoom range when the user select a preset interval
  useEffect(forgetUserZoomRange, [forgetUserZoomRange, selectedPeriodPreset]);

  // Make sure ECharts refresh its component when we change the height.
  useEffect(() => {
    if (chartRef.current && chart) {
      chartRef.current.style.height = `${height}px`;
      chart.resize();
    }
  }, [chart, height]);

  const onResize = useCallback(() => {
    if (chartRef.current && chart) {
      chart.resize();
    }
  }, [chart]);

  useResize(onResize);

  const [isEChartOptionsVisible, setEChartOptionsVisible] = useState(false);

  useEffect(() => {
    // show shift+scroll to zoom tips when zooming
    if (
      !isMobile &&
      features.includes('SHIFT_SCROLL_TO_ZOOM') &&
      chartRef.current &&
      chart
    ) {
      let timeoutId = null;
      const onWheel = debounce(() => {
        if (getTimesShownShiftScrollTop() >= 2) {
          return;
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        showShiftScrollTip(prev => {
          if (!prev) {
            incrementTimesShownShiftScrollTop();
          }
          return true;
        });
        timeoutId = setTimeout(() => showShiftScrollTip(false), 5000);
      }, 200);
      chart.on('datazoom', onWheel);
    }
  }, [chart, features, isMobile]);

  return (
    <Div
      onMouseOver={() => setDisplayLegendData(false)}
      onMouseLeave={() => setDisplayLegendData(true)}
    >
      {useIXICTradingViewPrice && <IXICTradingView />}
      <ChartContainer className="price-viewer-chart" ref={chartRef} />
      {isAdmin && (
        <ShortkeyListener
          handleKeys={['ctrl+d']}
          onKeyEvent={() => setEChartOptionsVisible(true)}
          shortkeys={adminShortKeys}
        />
      )}

      {isEChartOptionsVisible && chart && (
        <div>
          <h6>ECharts js options:</h6>
          <Suspense fallback={<LoadingImage loading />}>
            <ReactJsonLazy
              style={{ fontSize: 10 }}
              src={chart.getOption()}
              collapsed
            />
          </Suspense>
        </div>
      )}

      {markTooltipElement}

      <div
        className={classNames('shift-to-zoom-tip', {
          'shift-scroll-tip-visible': shiftScrollTipVisible,
        })}
      >
        <Trans i18nKey="shift-to-zoom-tip" components={{ bold: <b /> }} />{' '}
        <img src={ShiftScrollTip} />
      </div>
    </Div>
  );
};

export default PriceViewerChart;
